import type {
  MeasureEngagementReportResponse,
  MeasureExecutiveSummaryResponse,
  MeasureFunderReportResponse,
  MeasureImpactReportResponse,
  MeasureParticipationReportResponse,
} from "../../../redux/reducers/types";

export type MeasurePlatformReport =
  | MeasureExecutiveSummaryResponse
  | MeasureFunderReportResponse
  | MeasureEngagementReportResponse
  | MeasureParticipationReportResponse
  | MeasureImpactReportResponse;

const escapeCsv = (value: string | number | boolean | null | undefined) => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const row = (...cells: (string | number | boolean | null | undefined)[]) => cells.map(escapeCsv).join(",");

const formatPeriod = (from: string, to: string) =>
  `${new Date(from).toLocaleDateString()} – ${new Date(to).toLocaleDateString()}`;

export const buildMeasurePlatformReportCsv = (report: MeasurePlatformReport): string => {
  const lines: string[] = [];

  lines.push(row("Report", report.title));
  lines.push(row("Generated", new Date(report.generatedAt).toLocaleString()));
  lines.push(row("Scope", report.scope.description));
  lines.push(
    row(
      "Activity window",
      `${new Date(report.activityWindow.from).toLocaleDateString()} – ${new Date(report.activityWindow.to).toLocaleDateString()}`
    )
  );
  lines.push("");

  if (report.reportType === "executive-summary") {
    const m = report.platformSnapshot;
    lines.push("Platform snapshot");
    lines.push(row("Total organizations", m.totalOrganizations));
    lines.push(row("Total users", m.totalUsers));
    lines.push(row("Active users (30d)", m.activeUsers30));
    lines.push(row("Active users (60d)", m.activeUsers60));
    lines.push(row("Active users (90d)", m.activeUsers90));
    lines.push(row("New users this month", m.newUsersThisMonth));
    lines.push(row("Posts", m.totalPosts));
    lines.push(row("Interactions", m.totalInteractions));
    lines.push(row("Impact reports (all time)", m.reportsGenerated));
    lines.push("");

    lines.push("Portfolio in scope");
    lines.push(row("Organizations", report.portfolio.organizationCount));
    lines.push(row("Participation index", `${report.portfolio.participationIndex}%`));
    lines.push(row("Inactive", report.portfolio.inactive));
    lines.push(row("Needs support", report.portfolio.needsSupport));
    lines.push(row("Posts in window", report.portfolio.postsInWindow));
    lines.push(row("Org report activity in window", report.portfolio.orgReportsInWindow));
    lines.push("");

    if (report.funder.selectedFunder) {
      lines.push("Funder");
      lines.push(row("Selected funder", report.funder.selectedFunder));
      lines.push(row("Impact reports in window", report.funder.impactReportsInWindow ?? 0));
      if (report.funder.portfolioParticipation) {
        lines.push(row("Platform org count", report.funder.portfolioParticipation.orgCount));
        lines.push(
          row("Platform participation", `${report.funder.portfolioParticipation.participationIndex}%`)
        );
      }
      lines.push("");
    }
  } else if (report.reportType === "funder") {
    const f = report.funder;
    lines.push("Funder");
    lines.push(row("Name", f.name));
    lines.push(row("Organizations on platform", f.organizationsOnPlatform));
    lines.push(row("Platform participation", `${f.platformParticipationIndex}%`));
    lines.push(row("Impact reports in window", f.impactReportsInWindow));
    lines.push("");

    lines.push("Portfolio in scope (filtered)");
    lines.push(row("Organizations", report.portfolioInScope.organizationCount));
    lines.push(row("Participation index", `${report.portfolioInScope.participationIndex}%`));
    lines.push(row("Impact reports in window (org activity)", report.portfolioInScope.orgReportsInWindow));
    lines.push("");
  } else if (report.reportType === "engagement") {
    const reach = report.platformReach;
    lines.push("Platform reach");
    lines.push(row("Active users (30d)", reach.activeUsers30));
    lines.push(row("Active users (60d)", reach.activeUsers60));
    lines.push(row("Active users (90d)", reach.activeUsers90));
    lines.push(row("Posts (all time)", reach.totalPosts));
    lines.push(row("Interactions (all time)", reach.totalInteractions));
    lines.push(row("Active user definition", reach.activeUserDefinition));
    lines.push("");

    lines.push("Portfolio in scope");
    lines.push(row("Organizations", report.portfolio.organizationCount));
    lines.push(row("Participation index", `${report.portfolio.participationIndex}%`));
    lines.push(row("Posts in window", report.portfolio.postsInWindow));
    lines.push(row("Report activity in window", report.portfolio.orgReportsInWindow));
    lines.push(row("Inactive", report.portfolio.inactive));
    lines.push(row("Needs support", report.portfolio.needsSupport));
    lines.push(row("Methodology", report.methodology));
    lines.push("");

    if (report.byRegion.length > 0) {
      lines.push("By region");
      lines.push(row("Region", "Orgs", "High", "Medium", "Low", "Participation", "Posts", "Reports"));
      for (const r of report.byRegion) {
        lines.push(
          row(
            r.label,
            r.orgCount,
            r.high,
            r.medium,
            r.low,
            `${r.participationIndex}%`,
            r.postsInWindow,
            r.reportsInWindow
          )
        );
      }
      lines.push("");
    }

    if (report.cohortParticipation.length > 0) {
      lines.push("Cohort participation");
      lines.push(row("Cohort", "Orgs", "Participation index"));
      for (const c of report.cohortParticipation) {
        lines.push(row(c.name, c.orgCount, `${c.participationIndex}%`));
      }
      lines.push("");
    }
  } else if (report.reportType === "participation") {
    const totals = report.platformTotals;
    lines.push("Participation totals");
    lines.push(row("Events in window", totals.eventsInWindow));
    lines.push(row("Events (all time)", totals.eventsAllTime ?? ""));
    lines.push(row("Members in scope", totals.totalMembersInScope));
    lines.push(row("Active users (30d)", totals.activeUsers30));
    lines.push(row("Posts in window", totals.postsInWindow));
    lines.push(row("Impact report activity in window", totals.orgReportsInWindow));
    lines.push(row("Participation note", report.participationNote));
    lines.push("");

    lines.push("Portfolio in scope");
    lines.push(row("Organizations", report.portfolio.organizationCount));
    lines.push(row("Participation index", `${report.portfolio.participationIndex}%`));
    lines.push("");

    if (report.eventsByType.length > 0) {
      lines.push("Events by type");
      lines.push(row("Type", "Count"));
      for (const e of report.eventsByType) {
        lines.push(row(e.eventType, e.count));
      }
      lines.push("");
    }

    if (report.recentEvents.length > 0) {
      lines.push("Recent events");
      lines.push(row("Title", "Type", "Organization", "Starts"));
      for (const e of report.recentEvents) {
        lines.push(row(e.title, e.eventType, e.organizationName, new Date(e.startsAt).toLocaleString()));
      }
      lines.push("");
    }
  } else if (report.reportType === "impact") {
    lines.push("Impact summary");
    lines.push(row("Impact reports in window", report.impactReportsInWindow));
    lines.push(row("Impact reports (all time)", report.impactReportsAllTime));
    lines.push("");

    lines.push("Portfolio in scope");
    lines.push(row("Organizations", report.portfolio.organizationCount));
    lines.push(row("Participation index", `${report.portfolio.participationIndex}%`));
    lines.push(row("Posts in window", report.portfolio.postsInWindow));
    lines.push("");

    if (report.byFunder.length > 0) {
      lines.push("Impact reports by funder");
      lines.push(row("Funder", "Count"));
      for (const f of report.byFunder) {
        lines.push(row(f.funder, f.count));
      }
      lines.push("");
    }

    if (report.highlights.length > 0) {
      lines.push("Impact highlights");
      lines.push(row("Organization", "Funder", "Grant", "Period", "Narrative excerpt", "Filed"));
      for (const h of report.highlights) {
        lines.push(
          row(
            h.organizationName,
            h.funderName,
            h.grantName ?? "",
            formatPeriod(h.reportingPeriod.from, h.reportingPeriod.to),
            h.narrativeExcerpt,
            new Date(h.createdAt).toLocaleString()
          )
        );
      }
      lines.push("");
    }
  }

  if (report.reportType === "engagement") {
    lines.push(
      row(
        "Organization",
        "Region",
        "Report funder tag",
        "Cohort",
        "Onboarding",
        "Engagement",
        "Activity score",
        "Members",
        "Inactive",
        "Needs support",
        "Posts in window",
        "Reports in window"
      )
    );
    for (const o of report.organizations) {
      lines.push(
        row(
          o.name,
          o.region,
          o.funder,
          o.cohort ?? "",
          o.onboarding,
          o.engagement,
          o.activityScore,
          o.memberCount,
          o.inactive,
          o.needsSupport,
          o.postsInWindow,
          o.reportsInWindow
        )
      );
    }
  } else if (report.reportType === "participation") {
    lines.push(
      row(
        "Organization",
        "Region",
        "Funder",
        "Cohort",
        "Engagement",
        "Members",
        "Events in window",
        "Posts in window",
        "Reports in window"
      )
    );
    for (const o of report.organizations) {
      lines.push(
        row(
          o.name,
          o.region,
          o.funder,
          o.cohort ?? "",
          o.engagement,
          o.memberCount,
          o.eventsInWindow,
          o.postsInWindow,
          o.reportsInWindow
        )
      );
    }
  } else if (report.reportType === "impact") {
    lines.push(
      row(
        "Organization",
        "Region",
        "Funder",
        "Cohort",
        "Engagement",
        "Posts in window",
        "Reports in window"
      )
    );
    for (const o of report.organizations) {
      lines.push(
        row(
          o.name,
          o.region,
          o.funder,
          o.cohort ?? "",
          o.engagement,
          o.postsInWindow,
          o.reportsInWindow
        )
      );
    }
  } else {
    lines.push(
      row(
        "Organization",
        "Region",
        "Report funder tag",
        "Cohort",
        "Onboarding",
        "Engagement",
        "Inactive",
        "Needs support",
        "Posts in window",
        "Reports in window"
      )
    );
    const orgs = report.reportType === "funder" ? report.organizations : report.organizations;
    for (const o of orgs) {
      lines.push(
        row(
          o.name,
          o.region,
          o.funder,
          o.cohort ?? "",
          o.onboarding,
          o.engagement,
          o.inactive,
          o.needsSupport,
          o.postsInWindow,
          o.reportsInWindow
        )
      );
    }
  }

  return lines.join("\n");
};

export const downloadMeasurePlatformReportCsv = (report: MeasurePlatformReport) => {
  const csv = buildMeasurePlatformReportCsv(report);
  const slug = report.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${slug || "ignite-report"}-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
};
